import React, { useEffect, useRef, useState } from 'react';
import { ButtonsDiv, Divider, FormRow, InputField, SignUpForm } from './styles';
import InputPassword from 'common/InputPassword/InputPassword';
import Button from 'components/Buttom/Button';
import FieldMask from 'components/FieldMask';
import { Formik, Field, Form, FormikProps, FormikValues, FieldInputProps } from 'formik';
import SelectComponent from 'components/Select';
import { states, StatesEnum, StatesSelectProps, validateCNPJ, validateCPF } from '../../utils';
import ErrorMessage from 'components/ErrorMessage';
import yup from 'utils/validation';
import { validDDDs } from 'utils/basicUtils';
import FieldCEPComponent from 'components/FieldCEPComponent';
import { CIDADES_POR_ESTADO, Estados } from 'utils/location';
import SelectCityComponent from 'components/SelectCityComponent';
import MaskedPhoneInput from '../../../../../Config/components/MaskedPhone/MaskedPhoneInput';

const validationSchema = yup.object({
  document: yup
    .string()
    .trim()
    .matches(/^([\w\.\-\/]{18}|[\w\.\-\/]{14})$/, 'Informe um CNPJ completo')
    .test('is-valid-cnpj', 'Informe um CNPJ válido', value => validateCNPJ(value || ''))
    .required(),
  email: yup
    .string()
    .trim()
    .email()
    .matches(/^(?!\d+$).*/, 'O campo email não pode ser apenas números')
    .required(),
  password: yup.string().trim().min(8).required(),
  confirm_password: yup
    .string()
    .trim()
    .min(8)
    .oneOf([yup.ref('password'), undefined], 'As senhas não coincidem')
    .required(),
  extraInfo: yup.object({
    legalName: yup
      .string()
      .trim()
      .matches(/^(?!^\d+$)[\d\D]*$/, 'Este campo não pode conter somente números')
      .required(),
    responsibleName: yup
      .string()
      .trim()
      .matches(/^(?!^\d+$)[\d\D]*$/, 'Este campo não pode conter somente números')
      .required(),
    responsibleCpf: yup
      .string()
      .trim()
      .matches(/^([\w\.\-\/]{18}|[\w\.\-\/]{14})$/, 'Informe um CPF completo')
      .test('is-valid-cpf', 'Informe um CPF válido', value => validateCPF(value || ''))
      .required(),
    phone: yup
      .string()
      .trim()
      .required('Telefone é obrigatório')
      .matches(
        /^(\(\d{2}\)\s\d\s\d{4}-\d{4}|\(\d{2}\)\s\d{4}-\d{4})$/,
        'Telefone deve ter o formato (XX) X XXXX-XXXX ou (XX) XXXX-XXXX'
      )
      .test('is-valid-ddd', 'DDD inválido', function (value) {
        if (!value) return false;
        const ddd = value.match(/^\((\d{2})\)/);
        if (!ddd) return false;
        return validDDDs.includes(ddd[1]);
      })
      .test('not-all-same', 'Telefone não pode ter todos os números iguais', function (value) {
        if (!value) return false;
        const digits = value.replace(/\D/g, '');
        return !/^(\d)\1+$/.test(digits);
      }),
    tradeName: yup
      .string()
      .trim()
      .matches(/^(?!^\d+$)[\d\D]*$/, 'Este campo não pode conter somente números')
      .required(),
    address: yup.object({
      additionalData: yup.string().trim(),
      city: yup
        .string()
        .trim()
        .matches(/^(?!^\d+$)[\d\D]*$/, 'A cidade não pode conter somente números')
        .required(),
      neighborhood: yup
        .string()
        .trim()
        .matches(/^(?!^\d+$)[\d\D]*$/, 'O bairro não pode conter somente números')
        .required(),
      number: yup
        .string()
        .trim('Número não pode ter espaços vazios')
        // .matches(/^\d+$/, 'Número deve conter apenas dígitos')
        .required('Campo Obrigatório'),
      state: yup.string().trim().required(),
      street: yup
        .string()
        .trim()
        .matches(/^(?!\d+$).*/, 'O campo endereço não pode ser apenas números')
        .required(),
      zipCode: yup
        .string()
        .trim()
        // .matches(/^\d{5}-?\d{3}$/, 'CEP deve ter o formato XXXXX-XXX')
        .test('is-not-equal', 'CEP não pode conter todos os números iguais', function (value) {
          if (!value) return false;
          const cleanedValue = value.replace('-', '');
          return !/^(.)\1+$/.test(cleanedValue);
        })
        .required(),
    }),
  }),
});

const initialValues = {
  name: '',
  email: '',
  document: '',
  password: '',
  confirm_password: '',
  type: 'tour-pj',
  extraInfo: {
    role: 'tour-pj',
    cadastur: '',
    legalName: '',
    responsibleName: '',
    responsibleCpf: '',
    phone: '',
    tradeName: '',
    address: {
      additionalData: '',
      city: '',
      neighborhood: '',
      number: '',
      state: '',
      street: '',
      zipCode: '',
    },
  },
};

interface Props {
  onCancel: () => void;
  onSubmit: (values: FormikValues) => void;
}

const PjForm = (props: Props): JSX.Element => {
  const pjForm = useRef<FormikProps<FormikValues>>(null);
  const [selectedState, setSelectedState] = useState<string>('');
  const numberInputRef = useRef<HTMLInputElement>(null);
  const addressInputRef = useRef<HTMLInputElement>(null);
  const [cidades, setCidades] = useState<{ label: string; value: string }[]>([]);
  const { values, setFieldValue } = pjForm.current || {};

  const handleStateChange = async (estado: string) => {
    const cidadesEncontradas = CIDADES_POR_ESTADO[estado as Estados] || [];
    setCidades(
      cidadesEncontradas
        .map(cidade => ({
          label: cidade.nomeIBGE,
          value: cidade.nomeIBGE,
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
    );
  };

  useEffect(() => {
    setSelectedState(pjForm?.current?.values?.extraInfo.address.state);
    handleStateChange(pjForm?.current?.values?.extraInfo.address.state);
    if (pjForm?.current?.values?.extraInfo.address.city) {
      const cidadesEncontradas = CIDADES_POR_ESTADO[pjForm?.current?.values.extraInfo.address.state as Estados] || [];
      const cidadeExiste = cidadesEncontradas.some(cidade => cidade.nomeIBGE === values?.extraInfo.address.city);
      if (!cidadeExiste && setFieldValue) {
        setFieldValue('extraInfo.address.city', '');
      }
    }
  }, [pjForm?.current?.values?.extraInfo.address.state]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLFormElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      pjForm.current?.submitForm();
      if (pjForm.current?.values) {
        props.onSubmit(pjForm.current.values);
      }
    }
  };

  return (
    <Formik
      innerRef={pjForm}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={props.onSubmit}>
      <SignUpForm>
        <header>
          <h2>Dados da empresa:</h2>
          <span>2/2</span>
        </header>
        <Form onKeyDown={handleKeyDown}>
          <FormRow>
            <InputField id="cadastur-input">
              <div>
                <label htmlFor="">
                  CNPJ <span>*</span>
                </label>
                <FieldMask type="cnpj">
                  <Field type="text" name="document" placeholder="ex: xx.xxx.xxx/0001-xx" />
                </FieldMask>
              </div>
              <ErrorMessage name="document" />
            </InputField>
            <InputField id="company-name-input">
              <div>
                <label htmlFor="">
                  Nome Fantasia<span>*</span>
                </label>
                <Field name="extraInfo.tradeName" />
              </div>
              <ErrorMessage name="extraInfo.tradeName" />
            </InputField>
          </FormRow>
          <FormRow>
            <InputField id="social-reason-input">
              <div>
                <label htmlFor="">
                  Razão Social<span>*</span>
                </label>
                <Field name="extraInfo.legalName" />
              </div>
              <ErrorMessage name="extraInfo.legalName" />
            </InputField>
          </FormRow>
          <Divider />
          <h2>Dados do responsável:</h2>
          <FormRow>
            <InputField id="full-name-input">
              <div>
                <label htmlFor="">
                  Nome Completo<span>*</span>
                </label>
                <Field
                  name="extraInfo.responsibleName"
                  render={({ field, form }: { field: FieldInputProps<string>; form: FormikProps<FormikValues> }) => (
                    <input
                      {...field}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        form.setFieldValue('name', e.target.value);
                        form.setFieldValue('extraInfo.responsibleName', e.target.value);
                      }}
                    />
                  )}
                />
              </div>
              <ErrorMessage name="extraInfo.responsibleName" />
            </InputField>
            <InputField id="cpf-input">
              <div>
                <label htmlFor="">
                  CPF<span>*</span>
                </label>
                <FieldMask type="cpf">
                  <Field type="text" name="extraInfo.responsibleCpf" />
                </FieldMask>
              </div>
              <ErrorMessage name="extraInfo.responsibleCpf" />
            </InputField>
          </FormRow>
          <FormRow>
            <InputField id="cellphone-input">
              <div>
                <label htmlFor="">
                  Telefone ou Celular<span>*</span>
                </label>
                <FieldMask type="phone">
                  <Field name="extraInfo.phone" component={MaskedPhoneInput} />
                </FieldMask>
              </div>
              <ErrorMessage name="extraInfo.phone" />
            </InputField>
            <InputField id="email-input">
              <div>
                <label htmlFor="">
                  E-mail<span>*</span>
                </label>
                <FieldMask type="email">
                  <Field type="text" name="email" />
                </FieldMask>
              </div>
              <ErrorMessage name="email" />
            </InputField>
            <InputField id="cep-input">
              <div>
                <FieldCEPComponent
                  name="extraInfo.address.zipCode"
                  currentCity={pjForm.current?.values?.extraInfo?.address?.city}
                  label="CEP"
                  required
                  placeholder="ex: xxxxx-xxx"
                  value={pjForm.current?.values?.extraInfo.address.zipCode}
                  onChange={(field, value) => {
                    pjForm.current?.setFieldValue(field, value);
                    setSelectedState(pjForm?.current?.values.extraInfo.address.state);
                  }}
                  fieldMappings={[
                    {
                      formField: 'extraInfo.address.street',
                      responseField: 'logradouro',
                    },
                    {
                      formField: 'extraInfo.address.neighborhood',
                      responseField: 'bairro',
                    },
                    {
                      formField: 'extraInfo.address.city',
                      responseField: 'localidade',
                    },
                    {
                      formField: 'extraInfo.address.state',
                      responseField: 'uf',
                    },
                  ]}
                  nextFieldRef={numberInputRef}
                  fallbackFieldRef={addressInputRef}
                />
              </div>
              <ErrorMessage name="extraInfo.address.zipCode" />
            </InputField>
          </FormRow>
          <FormRow>
            <InputField id="address-input">
              <div>
                <label htmlFor="">
                  Endereço<span>*</span>
                </label>
                <Field innerRef={addressInputRef} name="extraInfo.address.street" />
              </div>
              <ErrorMessage name="extraInfo.address.street" />
            </InputField>
            <InputField id="number-input">
              <div>
                <label htmlFor="">
                  Número<span>*</span>
                </label>
                <Field innerRef={numberInputRef} name="extraInfo.address.number" />
              </div>
              <ErrorMessage name="extraInfo.address.number" />
            </InputField>
          </FormRow>
          <FormRow>
            <InputField id="complement-input">
              <div>
                <label htmlFor="">Complemento</label>
                <Field name="extraInfo.address.additionalData" />
              </div>
              <ErrorMessage name="extraInfo.address.additionalData" />
            </InputField>
            <InputField id="neighborhood-input">
              <div>
                <label htmlFor="">
                  Bairro<span>*</span>
                </label>
                <Field name="extraInfo.address.neighborhood" />
              </div>
              <ErrorMessage name="extraInfo.address.neighborhood" />
            </InputField>
          </FormRow>
          <FormRow>
            <InputField id="state-input">
              <div>
                <Field
                  validateOnBlur
                  component={({ field, form }: { field: any; form: any }) => (
                    <SelectComponent
                      label="Estado"
                      required
                      options={states.map(state => ({ label: state.label, value: state.value }))}
                      onChange={value => {
                        form.setFieldValue('extraInfo.address.state', value);
                        setSelectedState(value as StatesEnum);
                        handleStateChange(value as string); // Atualiza o estado e carrega as cidades
                      }}
                      name="extraInfo.address.state"
                      selected={field.value ? [field.value] : []} // Mantém o estado selecionado
                      //disabled={disabledInput}
                    />
                  )}
                  name="extraInfo.address.state"
                />
              </div>
              <ErrorMessage name="extraInfo.address.state" />
            </InputField>
            <InputField id="city-input">
              <div>
                <SelectCityComponent
                  label="Cidade"
                  name="extraInfo.address.city"
                  value={
                    cidades.find(cidade => cidade.value === pjForm.current?.values?.extraInfo.address.city)?.value || ''
                  }
                  options={cidades}
                  onChange={(selectedValue: string) => {
                    pjForm.current?.setFieldValue('extraInfo.address.city', selectedValue);
                  }}
                  onBlur={() => pjForm.current?.setFieldTouched('extraInfo.address.city', true)}
                  placeholderText="Selecione a cidade"
                  required
                />
              </div>
              <ErrorMessage name="extraInfo.address.city" />
            </InputField>
          </FormRow>
          <FormRow>
            <InputField id="password-input">
              <div>
                <label htmlFor="">
                  Senha<span>*</span>
                </label>
                <InputPassword name={'password'} />
              </div>
              <ErrorMessage name="password" />
            </InputField>
            <InputField id="password-input">
              <div>
                <label htmlFor="">
                  Confirmação de Senha<span>*</span>
                </label>
                <InputPassword name={'confirm_password'} />
              </div>
              <ErrorMessage name="confirm_password" />
            </InputField>
          </FormRow>
          <Divider />
          <ButtonsDiv>
            <Button onClick={props.onCancel} text="VOLTAR" />
            <Button
              onClick={() => {
                props.onCancel();
                pjForm?.current?.setValues(initialValues);
              }}
              text="CANCELAR"
            />
            <Button text="CRIAR" type="submit" />
          </ButtonsDiv>
        </Form>
      </SignUpForm>
    </Formik>
  );
};

export default PjForm;
